import React, { useState, useEffect, useMemo } from "react";
import ProfilePhotoPlaceholder from "../ProfilePhotoPlaceholder";
import { resolveSpeakerPhotoDisplayUrl } from "../../../utils/profilePictureUrl";

interface CircularPhotoProps {
  photo?: string | null;
  /** When set, resolves storage paths and blob URLs via the profile CDN helper. */
  personId?: string;
  alt?: string;
  className?: string;
  containerClassName?: string;
  onError?: (e: React.SyntheticEvent<HTMLImageElement, Event>) => void;
  loading?: "lazy" | "eager";
  objectPosition?: string;
}

const CircularPhoto: React.FC<CircularPhotoProps> = ({
  photo,
  personId,
  alt,
  className = "w-24 h-24 md:w-16 md:h-16 lg:w-20 lg:h-20",
  containerClassName = "",
  onError,
  loading = "lazy",
  objectPosition,
}) => {
  const [calculatedObjectPosition, setCalculatedObjectPosition] = useState<string>("center");
  const [imageBroken, setImageBroken] = useState(false);

  const displayPhoto = useMemo(
    () => resolveSpeakerPhotoDisplayUrl(personId, photo),
    [personId, photo],
  );

  useEffect(() => {
    setImageBroken(false);
  }, [displayPhoto]);

  useEffect(() => {
    if (displayPhoto && !objectPosition) {
      const img = new Image();
      img.onload = () => {
        const aspectRatio = img.width / img.height;
        if (aspectRatio < 1) {
          setCalculatedObjectPosition("top center");
        } else {
          setCalculatedObjectPosition("center");
        }
      };
      img.src = displayPhoto;
    } else if (objectPosition) {
      setCalculatedObjectPosition(objectPosition);
    }
  }, [displayPhoto, objectPosition]);

  const handleImgError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    setImageBroken(true);
    if (onError) {
      onError(e);
    }
  };

  if (!displayPhoto || imageBroken) {
    return (
      <div
        className={`${className} rounded-full bg-gray-300 flex items-center justify-center overflow-hidden ${containerClassName}`}
      >
        <ProfilePhotoPlaceholder />
      </div>
    );
  }

  return (
    <div
      className={`${className} rounded-full overflow-hidden flex items-center justify-center ${containerClassName}`}
    >
      <img
        src={displayPhoto}
        alt={alt || "Photo"}
        className="w-full h-full object-cover"
        style={{ objectPosition: calculatedObjectPosition }}
        loading={loading}
        onError={handleImgError}
      />
    </div>
  );
};

export default CircularPhoto;

